import { Link, NavLink, useLocation } from 'react-router-dom';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const navLinks = [
  { to: '/', label: 'Home' },
  { to: '/buy', label: 'Buy $AIML' },
  { to: '/refer', label: 'Refer & Earn' },
  { to: '/roadmap', label: 'Roadmap' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
];

export function Header() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-30 backdrop-blur-md bg-paper/80 border-b border-paper-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2">
          <Logo />
          <span className="font-display font-bold text-lg tracking-tight hidden sm:inline">
            AltCoinsMoney
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-medium rounded-xl transition-colors ${
                  isActive
                    ? 'text-violet-600 bg-violet-500/10'
                    : 'text-ink-700 hover:text-ink hover:bg-paper-200'
                }`
              }
              end={l.to === '/'}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        {/* Right cluster: connect + auth */}
        <div className="flex items-center gap-3">
          <div className="hidden sm:block">
            <ConnectButton showBalance={false} chainStatus="icon" accountStatus="address" />
          </div>
          {user ? (
            <div className="hidden md:flex items-center gap-2">
              <Link
                to="/dashboard"
                className="text-sm font-medium text-ink-700 hover:text-ink"
              >
                Dashboard
              </Link>
              {user.role === 'ADMIN' && (
                <Link
                  to="/admin"
                  className="text-sm font-medium text-violet-600 hover:text-violet-700"
                >
                  Admin
                </Link>
              )}
              <button
                onClick={() => logout()}
                className="text-sm text-ink-600 hover:text-ink"
              >
                Logout
              </button>
            </div>
          ) : (
            !location.pathname.startsWith('/auth') && (
              <div className="hidden md:flex items-center gap-2 text-sm">
                <Link to="/auth/login" className="text-ink-700 hover:text-ink font-medium">
                  Login
                </Link>
                <Link
                  to="/auth/signup"
                  className="px-3 py-1.5 rounded-xl bg-ink text-paper font-medium hover:bg-ink-700"
                >
                  Sign up
                </Link>
              </div>
            )
          )}

          <button
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 rounded-xl hover:bg-paper-200"
            aria-label="Menu"
          >
            {open ? '✕' : '☰'}
          </button>
        </div>
      </div>

      {/* Mobile nav */}
      {open && (
        <div className="lg:hidden border-t border-paper-300 bg-paper px-4 py-3 flex flex-col gap-1 animate-fade-in">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === '/'}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `px-3 py-2 text-sm rounded-xl ${
                  isActive ? 'text-violet-600 bg-violet-500/10' : 'text-ink-700'
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <div className="border-t border-paper-300 mt-2 pt-2 flex flex-col gap-2">
            <div className="sm:hidden">
              <ConnectButton showBalance={false} chainStatus="icon" />
            </div>
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  onClick={() => setOpen(false)}
                  className="px-3 py-2 text-sm rounded-xl text-ink-700 hover:bg-paper-200"
                >
                  Dashboard
                </Link>
                {user.role === 'ADMIN' && (
                  <Link
                    to="/admin"
                    onClick={() => setOpen(false)}
                    className="px-3 py-2 text-sm rounded-xl text-violet-600 hover:bg-violet-500/10"
                  >
                    Admin
                  </Link>
                )}
                <button
                  onClick={() => {
                    logout();
                    setOpen(false);
                  }}
                  className="px-3 py-2 text-sm rounded-xl text-ink-600 hover:bg-paper-200 text-left"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/auth/login"
                  onClick={() => setOpen(false)}
                  className="px-3 py-2 text-sm rounded-xl text-ink-700 hover:bg-paper-200"
                >
                  Login
                </Link>
                <Link
                  to="/auth/signup"
                  onClick={() => setOpen(false)}
                  className="px-3 py-2 text-sm rounded-xl bg-ink text-paper text-center"
                >
                  Sign up
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

function Logo() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="g" x1="0" y1="0" x2="32" y2="32" gradientUnits="userSpaceOnUse">
          <stop stopColor="#FF2EB6" />
          <stop offset="0.5" stopColor="#7C3AED" />
          <stop offset="1" stopColor="#2563EB" />
        </linearGradient>
      </defs>
      <rect width="32" height="32" rx="8" fill="url(#g)" />
      <path
        d="M9 22L16 10L23 22M11.5 18H20.5"
        stroke="white"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
